import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Gift, ChevronRight, ChevronLeft, Loader2, RotateCcw, Sparkles, ArrowRight } from "lucide-react";
import { useLang } from "../i18n";
import { giftFinder, formatPrice } from "../lib/api";
import { OBI, productImage } from "../assets/obi";

const STEP_BG = ["bg-pink-200", "bg-sky-200", "bg-yellow-200", "bg-emerald-200"];

const GiftFinderPage = () => {
  const { t, lang } = useLang();
  const [step, setStep] = useState(0); // 0 = intro, 1-4 = questions, 5 = results
  const [form, setForm] = useState({ occasion: "", age: "", budget: "", taste: "" });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const q1 = t("gift.q1_options");
  const q2 = t("gift.q2_options");
  const q3 = t("gift.q3_options");

  const submit = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await giftFinder({ ...form, lang });
      setResult(res);
      setStep(5);
    } catch (e) {
      console.error(e);
      setError(
        lang === "it"
          ? "OBI è inciampato! Riprova fra poco."
          : "OBI tripped! Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setStep(0);
    setForm({ occasion: "", age: "", budget: "", taste: "" });
    setResult(null);
    setError(null);
  };

  const renderOptions = (options, field) => (
    <div className="grid sm:grid-cols-2 gap-3">
      {options.map((opt) => (
        <button
          key={opt}
          data-testid={`gift-opt-${field}-${opt}`}
          onClick={() => {
            setField(field, opt);
            setStep((s) => s + 1);
          }}
          className={`cartoon-btn bg-white hover:bg-yellow-200 py-3 text-base justify-start text-left ${
            form[field] === opt ? "bg-yellow-300" : ""
          }`}
        >
          <span className="w-7 h-7 bg-pink-300 border-2 border-slate-900 rounded-full flex items-center justify-center text-sm font-bold shrink-0">
            ★
          </span>
          {opt}
        </button>
      ))}
    </div>
  );

  return (
    <div data-testid="gift-finder-page" className="min-h-screen bg-amber-50">
      {/* HERO */}
      <section className="bg-pink-200 border-b-4 border-slate-900 bg-dots">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20 grid lg:grid-cols-2 gap-10 items-center">
          <div className="space-y-5">
            <span className="inline-flex items-center gap-2 bg-yellow-300 border-4 border-slate-900 rounded-full px-4 py-1.5 font-bold text-sm cartoon-shadow-sm">
              <Gift className="w-4 h-4" /> {t("gift.hero_eyebrow")}
            </span>
            <h1 className="font-display font-bold text-5xl lg:text-6xl leading-[0.95] text-slate-900">
              {t("gift.hero_title")}
            </h1>
            <p className="text-lg text-slate-700 max-w-xl">{t("gift.hero_subtitle")}</p>
            {step === 0 && (
              <button
                data-testid="gift-start"
                onClick={() => setStep(1)}
                className="cartoon-btn bg-sky-400 hover:bg-sky-300 text-lg py-4 px-7"
              >
                {t("gift.cta_start")} <ArrowRight className="w-5 h-5" strokeWidth={3} />
              </button>
            )}
          </div>
          <div className="relative">
            <div className="absolute -top-4 -left-4 w-20 h-20 bg-yellow-400 rounded-full border-4 border-slate-900 cartoon-shadow hidden sm:block" />
            <div className="bg-gradient-to-br from-sky-200 to-yellow-100 border-4 border-slate-900 rounded-[2.5rem] cartoon-shadow-lg overflow-hidden">
              <img src={OBI.shopping} alt="OBI" className="w-full h-full object-cover animate-float" />
            </div>
          </div>
        </div>
      </section>

      {/* STEPS */}
      {step >= 1 && step <= 4 && (
        <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex items-center justify-between mb-6">
            <span className="font-display font-bold text-slate-900">
              {t("gift.step")} {step} {t("gift.of")} 4
            </span>
            <div className="flex items-center gap-2">
              {[1, 2, 3, 4].map((s) => (
                <span
                  key={s}
                  className={`w-3 h-3 rounded-full border-2 border-slate-900 ${
                    s <= step ? "bg-pink-400" : "bg-white"
                  }`}
                />
              ))}
            </div>
          </div>

          <div className={`cartoon-card ${STEP_BG[step - 1]} p-6 lg:p-10`}>
            <h2 className="font-display font-bold text-3xl lg:text-4xl text-slate-900 mb-6">
              {t(`gift.q${step}`)}
            </h2>

            {step === 1 && renderOptions(q1, "occasion")}
            {step === 2 && renderOptions(q2, "age")}
            {step === 3 && renderOptions(q3, "budget")}
            {step === 4 && (
              <div className="space-y-4">
                <textarea
                  data-testid="gift-taste"
                  rows={4}
                  value={form.taste}
                  onChange={(e) => setField("taste", e.target.value)}
                  placeholder={t("gift.q4_placeholder")}
                  className="cartoon-input w-full"
                />
                <button
                  data-testid="gift-submit"
                  disabled={!form.taste.trim() || loading}
                  onClick={submit}
                  className="cartoon-btn bg-pink-400 hover:bg-pink-300 w-full text-lg py-3 disabled:opacity-50"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" /> {t("gift.thinking")}
                    </>
                  ) : (
                    <>
                      {t("gift.submit")} <Sparkles className="w-5 h-5" />
                    </>
                  )}
                </button>
                {error && (
                  <p className="text-sm font-bold text-pink-700 bg-pink-100 border-2 border-pink-600 rounded-2xl px-3 py-2">
                    {error}
                  </p>
                )}
              </div>
            )}
          </div>

          <div className="flex justify-between items-center mt-6">
            {step > 1 ? (
              <button
                onClick={() => setStep((s) => s - 1)}
                className="cartoon-btn bg-white hover:bg-yellow-200 py-2 px-4 text-sm"
              >
                <ChevronLeft className="w-4 h-4" />
                {lang === "it" ? "Indietro" : "Back"}
              </button>
            ) : (
              <Link
                to="/"
                className="cartoon-btn bg-white hover:bg-yellow-200 py-2 px-4 text-sm"
              >
                <ChevronLeft className="w-4 h-4" />
                {lang === "it" ? "Home" : "Home"}
              </Link>
            )}
            <button
              onClick={reset}
              className="text-sm font-bold text-slate-600 hover:text-pink-600 flex items-center gap-1"
            >
              <RotateCcw className="w-3 h-3" /> {t("gift.try_again")}
            </button>
          </div>
        </section>
      )}

      {/* RESULTS */}
      {step === 5 && result && (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center mb-10">
            <img src={OBI.dice} alt="OBI" className="w-32 h-32 mx-auto animate-float" />
            <h2 className="font-display font-bold text-4xl lg:text-5xl text-slate-900 mt-4">
              {t("gift.results_title")}
            </h2>
            {result.intro && (
              <p className="text-lg text-slate-700 italic mt-4 max-w-2xl mx-auto">
                “{result.intro}”
              </p>
            )}
          </div>

          {result.suggestions.length === 0 ? (
            <div className="cartoon-card p-12 text-center max-w-xl mx-auto">
              <img src={OBI.empty} alt="OBI" className="w-44 h-44 mx-auto" />
              <p className="font-display font-bold text-2xl mt-4">{t("gift.empty_catalog")}</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
              {result.suggestions.map((s, i) => {
                const p = s.product;
                const bg = STEP_BG[i % STEP_BG.length];
                return (
                  <article
                    key={p.id}
                    data-testid={`gift-suggestion-${p.id}`}
                    className="cartoon-card overflow-hidden flex flex-col"
                  >
                    <Link to={`/product/${p.id}`} className="block">
                      <div className={`${bg} aspect-square border-b-4 border-slate-900 relative`}>
                        <img
                          src={productImage(p, i)}
                          alt={p.name}
                          className="w-full h-full object-cover"
                        />
                        <span className="absolute top-3 left-3 bg-yellow-300 border-2 border-slate-900 rounded-full px-3 py-1 text-xs font-bold cartoon-shadow-sm">
                          #{i + 1} {lang === "it" ? "scelto da OBI" : "OBI's pick"}
                        </span>
                      </div>
                    </Link>
                    <div className="p-5 flex flex-col gap-3 flex-1">
                      <h3 className="font-display font-bold text-xl text-slate-900 leading-tight">
                        <Link to={`/product/${p.id}`} className="hover:text-pink-600">
                          {p.name}
                        </Link>
                      </h3>
                      <p className="font-display font-bold text-2xl text-pink-600">
                        {formatPrice(p.price)}
                      </p>
                      {s.why && (
                        <div className="bg-amber-100 border-2 border-slate-900 rounded-2xl p-3">
                          <span className="bg-pink-400 border-2 border-slate-900 rounded-full px-2 py-0.5 text-[10px] font-bold mr-1">★ OBI</span>
                          <span className="text-sm italic text-slate-800 leading-relaxed">{s.why}</span>
                        </div>
                      )}
                      <Link
                        to={`/product/${p.id}`}
                        className="cartoon-btn bg-sky-300 hover:bg-sky-200 mt-auto text-sm py-2"
                      >
                        {t("gift.view_product")} <ChevronRight className="w-4 h-4" strokeWidth={3} />
                      </Link>
                    </div>
                  </article>
                );
              })}
            </div>
          )}

          <div className="text-center mt-10">
            <button onClick={reset} className="cartoon-btn bg-yellow-300 hover:bg-yellow-200">
              <RotateCcw className="w-4 h-4" /> {t("gift.try_again")}
            </button>
          </div>
        </section>
      )}
    </div>
  );
};

export default GiftFinderPage;
